import { type DataRequest } from "components/Shared/MaterialTable";

export enum CIReferralStatus {
    Open = 'O', // new
    Accepted = 'A', // pending
    Declined = 'D', // declined
    Assigned = 'S', // assigned
    Expired = 'E' // expired
}

export interface CIContact {
    name: string;
    email: string;
    phoneNumber: string;
    jobTitle: string;
}

export interface CILocation {
    addressLine1: string | null;
    addressLine2: string | null;
    city: string | null;
    state: string | null;
    country: string | null;
    postalCode: string | null;
}

export enum CIPriorityLevel {
    normal = 3,
    high = 2,
    critical = 1
}

export enum IncidentSettingType {
    WorkPlace = 900000000,
    NonWorkPlace = 900000001
}

export type CIModality = 'InPerson' | 'Telephonic' | 'Online' | 'Virtual' | 'In Person';

export interface CIReferralHighLevel {
    referralId: string;
    externalWorkOrderId: string;
    companyName: string;
    branchName?: string;
    divisionName?: string;
    priority: CIPriorityLevel;
    status: CIReferralStatus;
    bookingStartTime: string;
    bookingEndTime: string;
    modality: CIModality;
    location: CILocation | null;
    validUntil: string;
    category: string;
    timezone?: string;
    incidentType: string;
}

export interface CIReferral extends CIReferralHighLevel {
    externalId: string;
    externalCaseId: string;
    providerIds: number[];
    industry: string;
    bookingEndTime: string;
    numberOfImpacted: number;
    description: string;
    incidentSetting: IncidentSettingType;
    additionalInformation: string;
    language: string;
    acceptedOn?: string;
    declinedOn?: string;
    declinedReason?: string;
    assignedOn?: string;
    onSiteContact: CIContact;
    reportedByContact: CIContact;
}

export interface CICase {
    $type: 'critical_incident';
    externalWorkOrderId: string;
    externalCaseId: string;
    caseId: number;
    activityId: number;
    organizationName: string;
    serviceCode: string;
    presentingIssueCode: string;
    modalityCode: string;
    interventionStartDateTime: string;
    interventionEndDateTime: string;
    timezone: string | null;
    incidentReportStatus: IncidentReportStatus;
}

export interface CICaseOrganization {
    organizationId: number;
    company: string;
    branch: string;
    division: string;
    industryCode: number;
}

export interface CICaseIncident {
    serviceCode: string;
    presentingIssueCode: string;
    numberOfEmployee: number;
    description: string;
    reportStatus: IncidentReportStatus;
}

export interface CICaseIntervention {
    startDateTime: string;
    endDateTime: string;
    olsonName: string | null;
    modalityCode: string;
    languagePreferences: string[];
    location: string;
    onSiteInstruction: string;
}

export interface CICaseContact {
    name: string | null;
    email: string | null;
    phone: string | null;
}

export interface CICaseContacts {
    onSite: CICaseContact;
    requestor: CICaseContact;
}

export interface CICaseDetail {
    externalWorkOrderId: string;
    externalCaseId: string;
    caseId: number;
    activityId: number;
    organization: CICaseOrganization;
    incident: CICaseIncident;
    intervention: CICaseIntervention;
    contacts: CICaseContacts;
    providerId: number;
}

export interface ReferralsDataRequest extends DataRequest {
    filters: FiltersDefinition;
}

interface FiltersDefinition {
    status: ReferralsRequestFilterValue;
}

export enum ReferralsRequestFilterValue {
    All = 'ALL',
    New = 'O',
    Pending = 'A'
}

export enum CICasesFilterValue {
    All = 'ALL',
    UPCOMING = 'UPCOMING',
    HISTORY = 'HISTORY',
}

export enum IncidentReportStatus {
    Open = 'O',
    Submitted = 'S',
    InProgress = 'P',
}

export type IncidentReportYesNoOther = 'Y' | 'N' | 'O';

export interface IncidentReportInterventionSummary {
    actualStartDate: string;
    actualEndDate: string;
    supportModality: string;
    incidentDescription: string;
}

export interface IncidentReportDocumentation {
    preparationMeetingWithContact: IncidentReportYesNoOther;
    preparationMeetingWithContactOtherReason: string;
    wereEmployeesAwareAndEngaged: IncidentReportYesNoOther;
    wereEmployeesAwareAndEngagedOtherReason: string;
    wereReferralsExtended: IncidentReportYesNoOther;
    wereReferralsExtendedOtherReason: string;
    noOfWalkAbouts: number;
    walkAboutDescription: string;
    noOfOutreachRequests: number;
    outreachDescription: string;
    noOfConsultations: number;
    consultationDescription: string;
}

export interface IncidentReportIndividualSupportSessionParticipant {
    name: string;
    dateOfBirth: string | null;
    wasNatureOfSupportAndConfidentialityDiscussed: boolean | null;
    howIndividualPresented: string;
    interventionProvided: string;
    followUpNotes: string;
    declinedToProvideInformation: boolean;
    declinedReasonToProvideInformation: string;
    wasTheIndividualAtRisk: boolean | null;
    wasThereSupervisionOrConsultation: IncidentReportYesNoOther;
    wasThereSupervisionOrConsultationOtherReason: string;
    supervisionDate: string | null;
    supervisionOrConsultationOutcome: string;
    // AU-only additions (TPH-1906/1907/1908/1909) — best-effort draft, not yet confirmed
    // against a real /v2 contract. Non-AU never populates these; the four supervision
    // fields above stay in use for non-AU, while AU uses the nested `riskAssessment`
    // object instead.
    emailAddress: string;
    phoneNumber: string;
    riskAssessment: IncidentReportIndividualSupportSessionRiskAssessment | null;
    wereThereAnyFollowUpSessions: boolean | null;
    noOfFollowUpSessions: number;
    followUpSessions: IncidentReportIndividualSupportSessionFollowUpSession[];
}

// AU-only (TPH-1907/1908) — mirrors IncidentReportRiskAssessmentCore plus the
// supervision/consultation fields already captured inline on the participant for non-AU.
export interface IncidentReportIndividualSupportSessionRiskAssessment extends IncidentReportRiskAssessmentCore {
    wasThereSupervisionOrConsultation: IncidentReportYesNoOther;
    wasThereSupervisionOrConsultationOtherReason: string;
    supervisionDate: string | null;
    supervisionOrConsultationOutcome: string;
}

// AU-only (TPH-1909) — `sessionType` codes are a best guess (ID/ND/1W/2W/1M for
// Initial Day/Next Day/One Week/Two Week/One Month), not yet confirmed against the backend.
export interface IncidentReportIndividualSupportSessionFollowUpSession {
    sessionDate: string | null;
    sessionType: string;
    presentation: string;
    risk: string;
    intervention: string;
    recommendation: string;
}

export interface IncidentReportIndividualSupportSession {
    wereThereIndividualSupportSessions: boolean | null;
    noOfParticipants: number;
    participants: IncidentReportIndividualSupportSessionParticipant[];
}

export interface IncidentReportGroupServiceGroup {
    noOfIndividualParticipants: number | null;
    wasNatureOfSupportAndConfidentialityDiscussed: boolean | null;
    howGroupPresented: string;
    anyInterventionProvided: string;
    anyAdditionalSupportProvided: string;
}

export interface IncidentReportGroupService {
    wereThereGroupSessions: boolean;
    noOfGroups: number;
    groups: IncidentReportGroupServiceGroup[];
}

export interface IncidentReportOnsiteContact {
    wereYouRequestedToLeaveEarly: IncidentReportYesNoOther;
    wereYouRequestedToLeaveEarlyOtherReason: string;
    wasOrganizationContactSupportive: IncidentReportYesNoOther;
    wasOrganizationContactSupportiveOtherReason: string;
    wereThereBarriersToIntervention: IncidentReportYesNoOther;
    wereThereBarriersToInterventionOtherReason: string;
    organizationAndManagementSupportSummary: string;
    referrerTitle: string;
    referrerName: string;
    contactRemindedOfCiomAndSurvey: IncidentReportYesNoOther;
    contactRemindedOfCiomAndSurveyOtherReason: string;
    initialContactDate: string | null;
    contactInformation: string;
    onsiteContactDescription: string;
    agreedActions: string;
    followUpEapServicesAvailable: string;
    overlayingTrendsOrThemes: string;
}

export interface IncidentReportFollowUpContact {
    referralDate: string | null;
    referrerTitle: string;
    referrerName: string;
    contactInformation: string;
    description: string;
}

export interface IncidentReportFollowUps {
    wereThereAnyFollowUpSessions: boolean;
    noOfFollowUps: number;
    followUpContacts: IncidentReportFollowUpContact[];
}

export interface IncidentReportRecommendations {
    wereAnyRecommendationsDiscussed: IncidentReportYesNoOther;
    wereAnyRecommendationsDiscussedOtherReason: string;
    summaryOfRecommendations: string;
    anyServicesRecommended: string;
}

export type IncidentReportNotifiedPartyType =
    | 'clinicalSupervisor'
    | 'potentialVictim'
    | 'police'
    | 'childWelfare'
    | 'emergencyDepartment'
    | 'otherAgency'
    | 'family'
    | 'workplace'
    | 'learningInstitution'
    | 'none';

interface BaseIncidentReportNotifiedParty {
    partyType: IncidentReportNotifiedPartyType;
    notifiedDate: string | null;
}

export interface IncidentReportClinicalSupervisorNotifiedParty extends BaseIncidentReportNotifiedParty {
    partyType: 'clinicalSupervisor';
    wereOthersInformed: boolean | null;
}

export interface IncidentReportPotentialVictimNotifiedParty extends BaseIncidentReportNotifiedParty {
    partyType: 'potentialVictim';
    victimName: string;
    victimPhone: string;
    notes: string;
}

export interface IncidentReportPoliceNotifiedParty extends BaseIncidentReportNotifiedParty {
    partyType: 'police';
    officerName: string;
    policePhone: string;
    badgeNumber: string;
    conversationDetails: string;
}

export interface IncidentReportChildWelfareNotifiedParty extends BaseIncidentReportNotifiedParty {
    partyType: 'childWelfare';
    agencyName: string;
    representativeName: string;
    referenceNumber: string;
    conversationDetails: string;
}

export interface IncidentReportEmergencyDepartmentNotifiedParty extends BaseIncidentReportNotifiedParty {
    partyType: 'emergencyDepartment';
    departmentName: string;
    notes: string;
}

export interface IncidentReportOtherAgencyNotifiedParty {
    partyType: 'otherAgency';
    agencyName: string;
    notes: string;
}

export interface IncidentReportFamilyNotifiedParty extends BaseIncidentReportNotifiedParty {
    partyType: 'family';
    memberName: string;
    relationshipToClient: string;
    familyPhone: string;
    conversationDetails: string;
}

export interface IncidentReportWorkplaceNotifiedParty extends BaseIncidentReportNotifiedParty {
    partyType: 'workplace';
    contactName: string;
    jobTitle: string;
    workplacePhone: string;
    conversationDetails: string;
}

export interface IncidentReportLearningInstitutionNotifiedParty extends BaseIncidentReportNotifiedParty {
    partyType: 'learningInstitution';
    contactName: string;
    institutionDepartment: string;
    institutionPhone: string;
    conversationDetails: string;
}

export interface IncidentReportNoneNotifiedParty {
    partyType: 'none';
}

export type IncidentReportNotifiedParty =
    | IncidentReportClinicalSupervisorNotifiedParty
    | IncidentReportPotentialVictimNotifiedParty
    | IncidentReportPoliceNotifiedParty
    | IncidentReportChildWelfareNotifiedParty
    | IncidentReportEmergencyDepartmentNotifiedParty
    | IncidentReportOtherAgencyNotifiedParty
    | IncidentReportFamilyNotifiedParty
    | IncidentReportWorkplaceNotifiedParty
    | IncidentReportLearningInstitutionNotifiedParty
    | IncidentReportNoneNotifiedParty;

// Shared between the standalone (non-AU) RiskAssessments participant and the AU-only
// risk assessment embedded inside an IndividualSupportSession participant — the field
// components that render these (RiskFactorsSection, ActionsTakenSection, NotifiedPartyFields)
// only ever touch this common subset, which is what lets them be reused by both call sites.
export interface IncidentReportRiskAssessmentCore {
    riskOfSuicide: boolean | null;
    suicideRiskFactors: string[];
    riskOfSelfHarm: boolean | null;
    selfHarmRiskFactors: string[];
    riskOfHarmToFromOthers: boolean | null;
    harmToFromOthersRiskFactors: string[];
    riskDescription: string;
    riskFactors: string[];
    hasDutyToReport: IncidentReportYesNoOther;
    dutyToReportDescription: string;
    notifiedPartiesDetails: string;
    measureTakenToReduceRisks: string;
    agreementAgainstSuicide: string;
    agreementAgainstSuicideDescription: string;
    selectedNotifiedParties: IncidentReportNotifiedPartyType[];
    partiesNotified: IncidentReportNotifiedParty[];
}

export interface IncidentReportRiskAssessmentParticipant extends IncidentReportRiskAssessmentCore {
    name: string;
    dateOfBirth: string | null;
    phoneNumber: string;
    emailAddress: string;
}

export interface IncidentReportRiskAssessments {
    wereThereAnyAtRiskIndividuals: boolean;
    noOfParticipants: number;
    participants: IncidentReportRiskAssessmentParticipant[];
}

// AU-only section (TPH-1903) — field shapes are a best-effort draft from the AC tables,
// not yet confirmed against a real /v2 contract
export interface IncidentReportReferringManagerContact {
    date: string | null;
    details: string;
}

export interface IncidentReportReferringManager {
    wasThereContactWithReferringManager: boolean | null;
    contacts: IncidentReportReferringManagerContact[];
}

// AU-only section (TPH-1890) — field shapes are a best-effort draft from the AC tables,
// not yet confirmed against a real /v2 contract. `feeLine` stays a plain string until
// the ticket's unresolved 5th dropdown value is confirmed.
export interface IncidentReportDeliveryHoursEntry {
    date: string | null;
    durationHours: number | null;
    activity: string;
    feeLine: string;
    completed: boolean | null;
}

export interface IncidentReportDeliveryHours {
    entries: IncidentReportDeliveryHoursEntry[];
}

export type IncidentReportSectionType =
    | 'interventionSummary'
    | 'incidentDocumentation'
    | 'individualSupportSession'
    | 'groupService'
    | 'onsiteContact'
    | 'followUp'
    | 'recommendation'
    | 'riskAssessment'
    | 'referringManager'
    | 'deliveryHours';

export interface IncidentReport {
    id: string;
    caseId: number;
    activityId: number;
    status: IncidentReportStatus;
    submittedOn: string;
    submittedBy: string;
    createdOn: string;
    createdBy: string;
    modifiedOn: string;
    modifiedBy: string;
    interventionSummary: IncidentReportInterventionSummary;
    incidentDocumentation: IncidentReportDocumentation;
    individualSupportSession: IncidentReportIndividualSupportSession;
    groupService: IncidentReportGroupService;
    onsiteContact: IncidentReportOnsiteContact;
    followUp: IncidentReportFollowUps;
    recommendation: IncidentReportRecommendations;
    riskAssessment: IncidentReportRiskAssessments;
    referringManager: IncidentReportReferringManager;
    deliveryHours: IncidentReportDeliveryHours;
}
